import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Slider from '@mui/material/Slider';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';

const glassStyle = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: '16px',
  backdropFilter: 'blur(12px)',
};

const SliderRow = ({ label, value, onChange, min, max, step, unit }) => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, fontSize: '0.8rem' }}>
        {value} {unit}
      </Typography>
    </Box>
    <Slider
      value={value}
      onChange={(_, v) => onChange(v)}
      min={min}
      max={max}
      step={step}
      size="small"
      sx={{
        color: 'rgba(255,255,255,0.7)',
        '& .MuiSlider-thumb': { backgroundColor: 'white' },
        '& .MuiSlider-track': { backgroundColor: 'rgba(255,255,255,0.8)' },
        '& .MuiSlider-rail': { backgroundColor: 'rgba(255,255,255,0.2)' },
      }}
    />
  </Box>
);

/**
 * Collapsible settings panel for timer durations.
 *
 * Props:
 *  settings: { focusDuration, shortBreak, longBreak, longBreakAfter }
 *  onSave: (settings) => void
 *  hasTaskOverride: boolean — true when an active task has custom timer settings
 */
const TimerSettingsPanel = ({ settings, onSave, hasTaskOverride, isPushSubscribed, onSubscribePush, onUnsubscribePush, onTestPush }) => {
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState(null);

  const current = local || settings || {};

  const handleOpen = () => {
    setLocal({ ...settings });
    setOpen(true);
  };
  const handleClose = () => {
    setLocal(null);
    setOpen(false);
  };
  const handleSave = () => {
    onSave(local);
    setLocal(null);
    setOpen(false);
  };

  const set = (key) => (val) => setLocal((prev) => ({ ...prev, [key]: val }));

  return (
    <Box sx={{ width: '100%', maxWidth: 360, mx: 'auto', mt: 2 }}>
      <Button
        onClick={open ? handleClose : handleOpen}
        startIcon={<SettingsRoundedIcon sx={{ fontSize: 16 }} />}
        endIcon={open ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
        sx={{
          color: 'rgba(255,255,255,0.5)',
          fontSize: '0.8rem',
          textTransform: 'none',
          '&:hover': { color: 'rgba(255,255,255,0.9)', backgroundColor: 'transparent' },
          width: '100%',
          justifyContent: 'center',
        }}
      >
        Settings
        {hasTaskOverride && (
          <Box
            component="span"
            title="Active task has custom timer settings"
            sx={{
              ml: 0.7,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.3,
              background: 'rgba(229,57,53,0.15)',
              border: '1px solid rgba(229,57,53,0.3)',
              borderRadius: '8px',
              px: 0.7,
              py: 0.1,
              fontSize: '0.62rem',
              color: '#e57373',
              fontWeight: 700,
              lineHeight: 1.5,
            }}
          >
            ⏱ task
          </Box>
        )}
      </Button>

      <Collapse in={open}>
        <Box sx={{ ...glassStyle, p: 3, mt: 1 }}>
          {hasTaskOverride && (
            <Box
              sx={{
                mb: 2,
                px: 1.5,
                py: 0.9,
                borderRadius: '10px',
                background: 'rgba(229,57,53,0.08)',
                border: '1px solid rgba(229,57,53,0.2)',
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
              }}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#e53935', flexShrink: 0 }} />
              <Typography sx={{ color: 'rgba(229,57,53,0.85)', fontSize: '0.72rem', lineHeight: 1.4 }}>
                Active task has custom durations — values shown are effective overrides.
                Edit the task (✏️) to change task-specific settings.
              </Typography>
            </Box>
          )}
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 1 }}
          >
            Durations
          </Typography>

          <Box sx={{ mt: 2 }}>
            <SliderRow
              label="Focus Session"
              value={Math.round((current.focusDuration || 1500) / 60)}
              onChange={(v) => set('focusDuration')(v * 60)}
              min={5} max={60} step={5} unit="min"
            />
            <SliderRow
              label="Short Break"
              value={Math.round((current.shortBreak || 300) / 60)}
              onChange={(v) => set('shortBreak')(v * 60)}
              min={1} max={15} step={1} unit="min"
            />
            <SliderRow
              label="Long Break"
              value={Math.round((current.longBreak || 900) / 60)}
              onChange={(v) => set('longBreak')(v * 60)}
              min={5} max={30} step={5} unit="min"
            />
          </Box>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />

          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 1 }}
          >
            Cycle
          </Typography>
          <Box sx={{ mt: 2 }}>
            <SliderRow
              label="Long break after"
              value={current.longBreakAfter || 4}
              onChange={set('longBreakAfter')}
              min={2} max={8} step={1} unit="sessions"
            />
          </Box>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />

          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 1 }}
          >
            Background Alerts (iPhone / PWA)
          </Typography>
          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {isPushSubscribed ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: '#4ade80', fontSize: '0.8rem', fontWeight: 600 }}>
                    ✓ Web Push Enabled
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    onClick={onUnsubscribePush}
                    sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', textTransform: 'none', minWidth: 'auto' }}
                  >
                    Disable
                  </Button>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onTestPush}
                  sx={{
                    borderColor: 'rgba(74,222,128,0.3)',
                    color: '#4ade80',
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    '&:hover': { borderColor: '#4ade80', background: 'rgba(74,222,128,0.1)' },
                  }}
                >
                  🔔 Send Test Background Alert
                </Button>
              </>
            ) : (
              <Button
                size="small"
                variant="contained"
                onClick={onSubscribePush}
                sx={{
                  background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
                  color: '#111',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  '&:hover': { background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' },
                }}
              >
                Enable Web Push Alerts
              </Button>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              fullWidth
              size="small"
              sx={{
                borderColor: 'rgba(255,255,255,0.2)',
                color: 'rgba(255,255,255,0.6)',
                '&:hover': { borderColor: 'rgba(255,255,255,0.4)' },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              fullWidth
              size="small"
              sx={{ backgroundColor: 'rgba(255,255,255,0.15)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)' } }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default TimerSettingsPanel;
